import { useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { EnvelopeSimple, ArrowRight, CheckCircle } from "@phosphor-icons/react";
import PublicFooter from "@/components/PublicFooter";

const LOGO_URL = "https://customer-assets-gfyr7b9c.emergentagent.net/job_francophone-ops/artifacts/1lh4l6xi_3%20%281%29.PNG";
const portalApi = axios.create({ baseURL: API, withCredentials: true });

export default function PortalLogin() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!email || !email.includes("@")) return toast.error("Enter a valid email");
    setSending(true);
    try {
      await portalApi.post("/portal/auth/request-link", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="portal-login-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-6 justify-center">
          <img src={LOGO_URL} alt="Unity Language Group" className="h-10 w-auto" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-red-700 font-bold">Unity Language Group</div>
            <div className="text-sm text-slate-500">Interpreter portal</div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm">
          {sent ? (
            <div className="text-center py-4" data-testid="portal-login-sent">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 mb-4">
                <CheckCircle size={32} weight="fill" />
              </div>
              <div className="text-lg font-bold text-slate-900 mb-1">Check your email</div>
              <div className="text-sm text-slate-500">
                If <span className="font-mono text-slate-700">{email}</span> is on our interpreter roster,
                we've sent you a sign-in link. It expires in 15 minutes.
              </div>
              <button
                onClick={() => { setSent(false); setEmail(""); }}
                className="text-xs text-slate-500 hover:text-slate-900 mt-6 underline"
              >
                Use a different email
              </button>
            </div>
          ) : (
            <>
              <div className="text-xl font-bold text-slate-900 mb-1">Sign in</div>
              <div className="text-sm text-slate-500 mb-6">
                Enter the email on your interpreter file. We'll email you a one-tap sign-in link — no password needed.
              </div>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-xs">Email</Label>
                  <div className="relative mt-1.5">
                    <EnvelopeSimple size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      id="email"
                      data-testid="portal-login-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="pl-9"
                      autoFocus
                    />
                  </div>
                </div>
                <Button
                  data-testid="portal-login-submit"
                  type="submit"
                  disabled={sending}
                  className="w-full bg-red-700 hover:bg-red-800 text-white"
                >
                  {sending ? "Sending link…" : (<>Email me a sign-in link <ArrowRight size={14} className="ml-1.5" /></>)}
                </Button>
              </form>
            </>
          )}
        </div>

        <div className="text-center text-[11px] text-slate-400 mt-6">
          Not an interpreter yet? <a href="/apply" className="underline">Apply here</a>.
        </div>
        <PublicFooter />
      </div>
    </div>
  );
}
